import CloseIcon from '@mui/icons-material/Close';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SignaturePad } from '../../../components/signature/SignaturePad';
import { SlideUpTransition } from '../../../components/transitions/slide-up-transition';
import { useNotification } from '../../../hooks/use-notification';
import { useUserSignature } from '../../../hooks/use-user-signature';
import { MODULE_ACCENT_COLORS } from '../../../theme/module-colors';

const ACCENT = MODULE_ACCENT_COLORS.documents.main;

interface ConfigureSignatureDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfigured: () => void;
}

export function ConfigureSignatureDialog({
  open,
  onCancel,
  onConfigured,
}: ConfigureSignatureDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const { isSaving, saveSignature } = useUserSignature();

  const handleSave = useCallback(
    async (dataUrl: string) => {
      try {
        await saveSignature(dataUrl);
        showSuccess(t('signature.signatureSaved'));
        onConfigured();
      } catch {
        showError(t('signature.signatureError'));
      }
    },
    [saveSignature, showSuccess, showError, onConfigured, t],
  );

  const handleClose = useCallback(() => {
    if (isSaving) {
      return;
    }
    onCancel();
  }, [isSaving, onCancel]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      slots={{ transition: isMobile ? SlideUpTransition : undefined }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pr: 6,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${ACCENT}33, ${ACCENT}0D)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <DrawOutlinedIcon sx={{ fontSize: 22, color: ACCENT }} />
        </Box>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {t('documents.noSignatureButton')}
        </Typography>
        <IconButton
          aria-label={t('common.close')}
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          {t('documents.noSignatureTitle')}
        </Typography>

        <SignaturePad
          height={isMobile ? 220 : 200}
          onSave={handleSave}
          disabled={isSaving}
          saveButtonText={isSaving ? t('signature.savingLabel') : t('signature.saveSignature')}
        />

        <Button
          variant="text"
          fullWidth
          onClick={handleClose}
          disabled={isSaving}
          sx={{ minHeight: 48 }}
        >
          {t('common.cancel')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
